import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import useNotification from '../../../core/hooks/use_notification';
import { deleteDeliveryNotificationNotificationData } from '../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import { DeleteButton, DuplicateButton } from '../../common/button';
import { getDeliveryNotificationsState } from '../../../features/communication/get_delivery_notifications';
import { deleteDeliveryNotificationAction } from '../../../features/communication/delete_delivery_notification';
import { GET_ROUTE_ROUTE_DELIVERY_NOTIFICATIONS_EDIT, ROUTE_DELIVERY_NOTIFICATIONS_NEW } from '../../../core/constants';
import { FormRouteEnum } from '../../../core/enums/common/route';

const DeliveryNotificationsList = ({ refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const getDeliveryNotifications = useSelector(getDeliveryNotificationsState);

    const [loadingDeleteIds, setLoadingDeleteIds] = useState([]);

    const onDeleteDeliveryNotification = (deliveryNotificationId) => {
        setLoadingDeleteIds(prev => [...prev, deliveryNotificationId]);

        dispatch(deleteDeliveryNotificationAction(deliveryNotificationId))
            .then(response => {
                showNotification(deleteDeliveryNotificationNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) refreshList();
            })
            .finally(() => {
                setLoadingDeleteIds(prev => prev.filter(id => id !== deliveryNotificationId));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Nazwa robocza</TableCell>
                        <TableCell>Treść</TableCell>
                        <TableCell align="right" sx={ { minWidth: 140 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { getDeliveryNotifications?.data?.content?.map(deliveryNotification => (
                        <TableRow key={ deliveryNotification?.id } hover>
                            <TableCell sx={ { fontWeight: 500 } }>
                                { deliveryNotification?.workingName }
                            </TableCell>
                            <TableCell>
                                <Box sx={ { minWidth: 280, maxWidth: 600 } }>
                                    { deliveryNotification?.translatedMessages.map(message => (
                                        <Accordion
                                            key={ message.languageCode }
                                            disableGutters
                                            elevation={ 0 }
                                            square
                                            sx={ {
                                                border: theme => `1px solid ${ theme.palette.divider }`,
                                                borderBottom: 0,
                                                '&:last-of-type': { borderBottom: theme => `1px solid ${ theme.palette.divider }` },
                                                '&:before': { display: 'none' },
                                                backgroundColor: 'transparent'
                                            } }
                                        >
                                            <AccordionSummary
                                                expandIcon={ <ExpandMoreIcon fontSize="small"/> }
                                                sx={ { minHeight: 36, '& .MuiAccordionSummary-content': { my: 1 } } }
                                            >
                                                <Typography variant="body2" sx={ { fontWeight: 500 } }>
                                                    <b>{ message.languageCode }</b> - { message.title }
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={ { pt: 0, pb: 1.5, px: 2 } }>
                                                <Typography variant="caption" sx={ { wordBreak: 'break-word' } }>
                                                    { message.body }
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    )) }
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={ 1 }>
                                    <Tooltip title="Edytuj">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={ () => navigate(GET_ROUTE_ROUTE_DELIVERY_NOTIFICATIONS_EDIT(deliveryNotification?.id), { state: { action: FormRouteEnum.EDIT } }) }
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>

                                    <Box title="Duplikuj">
                                        <DuplicateButton
                                            onClick={ () => navigate(ROUTE_DELIVERY_NOTIFICATIONS_NEW, {
                                                state: {
                                                    action: FormRouteEnum.NEW,
                                                    deliveryNotificationIdToDuplicate: deliveryNotification?.id
                                                }
                                            }) }
                                        />
                                    </Box>

                                    <Box title="Usuń">
                                        <DeleteButton
                                            onDeleteClick={ () => onDeleteDeliveryNotification(deliveryNotification?.id) }
                                            isLoading={ loadingDeleteIds.includes(deliveryNotification?.id) }
                                        />
                                    </Box>
                                </Stack>
                            </TableCell>
                        </TableRow>
                    )) }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DeliveryNotificationsList;